import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { trpc } from '@/lib/trpc';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

export function NotificationsBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Una sola query, cada 2 minutos y al volver a la pestaña. Antes eran dos
  // (lista + contador) cada 30 s: 240 llamadas por hora por pestaña abierta,
  // el 45 % de todo el tráfico tRPC de prod (telemetría del 30/09/2026), sin
  // relación con cuántas notificaciones tenga nadie. El contador de no leídos
  // sale de la lista que ya llega; getUnreadCount volvía a leer hasta 100
  // notificaciones solo para contarlas.
  const { data: notifications = [], refetch } = trpc.notifications.getNotifications.useQuery(
    { limit: 50 },
    { refetchInterval: 2 * 60 * 1000, refetchOnWindowFocus: true }
  );
  // Tope en 50 a propósito: el badge muestra "9+" por encima de nueve.
  const unreadCount = notifications.filter((n) => n.read === 0).length;

  // Mutation to mark as read
  const markAsReadMutation = trpc.notifications.markAsRead.useMutation({
    onSuccess: () => {
      refetch();
    },
  });

  // Mutation to mark all as read
  const markAllAsReadMutation = trpc.notifications.markAllAsRead.useMutation({
    onSuccess: () => {
      refetch();
    },
  });

  const resolveActionUrl = (notification: typeof notifications[number]): string | undefined => {
    if (notification.actionUrl) return notification.actionUrl;
    // Fallback for notifications created before actionUrl was added
    const meta = notification.metadata as Record<string, any> | undefined;
    if (meta?.approvalId || meta?.altaId) return '/admin/devices/approval';
    return undefined;
  };

  const handleNotificationClick = (notificationId: string) => {
    const notification = notifications.find(n => n.notificationId === notificationId);
    if (!notification) return;
    if (notification.read === 0) {
      markAsReadMutation.mutate({ notificationId });
    }
    const url = resolveActionUrl(notification);
    if (url) {
      setOpen(false);
      navigate(url);
    }
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'border-l-4 border-green-500';
      case 'error':
        return 'border-l-4 border-red-500';
      case 'warning':
        return 'border-l-4 border-yellow-500';
      case 'info':
      default:
        return 'border-l-4 border-blue-500';
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t('common.a11y.notifications')} className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[calc(100vw-1rem)] sm:w-96">
        <div className="flex items-center justify-between px-2 py-1.5">
          <DropdownMenuLabel>{t('notifications.title')}</DropdownMenuLabel>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={handleMarkAllAsRead}
              disabled={markAllAsReadMutation.isPending}
            >
              {t('notifications.mark_all_read')}
            </Button>
          )}
        </div>
        <DropdownMenuSeparator />

        <div className="max-h-[400px] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Bell className="h-12 w-12 mb-2 opacity-50" />
              <p className="text-sm">{t('notifications.empty')}</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.notificationId}
                className={`p-4 ${getNotificationColor(notification.type)} ${
                  notification.read === 0 ? 'bg-muted/50' : ''
                } ${resolveActionUrl(notification) ? 'cursor-pointer' : 'cursor-default'}`}
                onClick={() => handleNotificationClick(notification.notificationId)}
              >
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <h4 className="text-sm font-semibold">{notification.title}</h4>
                    {notification.read === 0 && (
                      <div className="ml-2 h-2 w-2 rounded-full bg-blue-500 shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {notification.message}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {formatDistanceToNow(new Date(notification.createdAt), {
                      addSuffix: true,
                      locale: es,
                    })}
                  </p>
                </div>
              </DropdownMenuItem>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
